import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, RefreshCw, Settings, X } from 'lucide-react'
import { listPOs, resyncPOApprovals, scanPOApprovals, setPOApproval } from '../../api/purchase'
import { useToast } from '../../components/Toast.jsx'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money } from '../sales/salesShared'
import PurchaseTabs from './PurchaseTabs'

// Purchase orders over the approval limit wait here for an admin.
export default function POApprovalInbox() {
  const { notify } = useToast()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState('')

  const refresh = async () => {
    try {
      const pending = await scanPOApprovals()
      if (Array.isArray(pending) && pending.length) { setRows(pending); return }
    } catch { /* fall back to the order list below */ }
    try {
      const all = await listPOs()
      setRows((all || []).filter((p) => String(p.approval_status || '').trim().toLowerCase() === 'pending'))
    } catch {
      setRows([])
    }
  }

  useEffect(() => { refresh().finally(() => setLoading(false)) }, [])

  const decide = async (poId, status) => {
    const po = docNo('po', rows.find((r) => r.po_id === poId)?.po_no) || 'The order'
    setError('')
    setBusy(poId)
    try {
      await setPOApproval(poId, status, note)
      notify(`${po} ${status === 'Approved' ? 'approved' : 'rejected'}`)
      await refresh()
    } catch (e) {
      setError(e.message || `Could not ${status === 'Approved' ? 'approve' : 'reject'} ${po}.`)
    } finally {
      setBusy(null)
    }
  }

  const recheck = async () => {
    setError('')
    setBusy('recheck')
    try {
      await resyncPOApprovals()
      await refresh()
      notify('Approval list rechecked')
    } catch (e) {
      setError(e.message || 'Could not recheck approvals.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="ci-page">
      <PurchaseTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Approvals</h1>
            <div className="ci-title-meta">Purchase orders over your approval limit wait here before they can be received.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" onClick={recheck} disabled={!!busy}><RefreshCw size={14} />Recheck</button>
          <Link className="ci-btn ci-btn-ghost" to="/purchase/approval-settings"><Settings size={14} />Approval limit</Link>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="ci-toolbar">
        <label className="ci-search approval-note" aria-label="Note">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to save with your decision (optional)" />
        </label>
      </div>

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Vendor</th>
                <th>Ordered</th>
                <th className="num">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5"><Spinner label="Loading approvals…" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="5"><div className="ci-empty">Nothing is waiting for approval.</div></td></tr>
              ) : rows.map((r) => (
                <tr key={r.po_id}>
                  <td><Link className="ci-je-link" to={`/purchase/po-detail/${r.po_id}`}>{docNo('po', r.po_no)}</Link></td>
                  <td className="ci-strong">{r.vendor_name || '–'}</td>
                  <td className="ci-muted-cell">{fmtDay(r.po_date) || '–'}</td>
                  <td className="num ci-total">{money(r.total_amount)}</td>
                  <td className="num">
                    <div className="row-actions">
                      <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={!!busy} onClick={() => decide(r.po_id, 'Rejected')}><X size={13} />Reject</button>
                      <button type="button" className="ci-btn ci-btn-primary ci-btn-sm" disabled={!!busy} onClick={() => decide(r.po_id, 'Approved')}><Check size={13} />Approve</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
